'use client';

import { ReactNode, createContext, useContext, useEffect } from 'react';
import { useLocalObservable } from 'mobx-react-lite';
import { IFlightsStores, createFlightStores } from '../createFlightStores';
import { FlightListScope } from '../../overview/domain/constants/FlightListScope';
import { AirportCodes } from '../../../../types/airports';

const FlightStoresContext = createContext<IFlightsStores | null>(null);
function getInitialState(
	scope?: FlightListScope,
	query?: string,
	airportCode?: AirportCodes,
) {
	const stores = createFlightStores();

	if (scope) {
		stores.flightListStore.setScope(scope);
	}

	if (query != null) {
		stores.flightListStore.setQuery(query);
		stores.flightSuggestionStore.setQuery(query);
	}

	if (airportCode) {
		stores.flightListStore.setSelectedAirportCode(airportCode);
	}

	return stores;
}

export function FlightStoresProvider({
	children,
	scope,
	query,
	airportCode,
}: {
	children: ReactNode;
	scope?: FlightListScope;
	query?: string;
	airportCode?: AirportCodes;
}) {
	const state = useLocalObservable(() =>
		getInitialState(scope, query, airportCode),
	);

	useEffect(() => {
		if (scope) {
			state.flightListStore.setScope(scope);
		}
	}, [scope]);

	useEffect(() => {
		const newQuery = query ?? '';
		state.flightListStore.setQuery(newQuery);
		state.flightSuggestionStore.setQuery(newQuery);
	}, [query]);

	useEffect(() => {
		if (airportCode) {
			state.flightListStore.setSelectedAirportCode(airportCode);
		}
	}, [airportCode]);

	return (
		<FlightStoresContext.Provider value={state}>
			{children}
		</FlightStoresContext.Provider>
	);
}

export function useFlightStores() {
	const context = useContext(FlightStoresContext);
	if (context === null) {
		throw new Error(
			'useFlightStores must be used within a FlightStoresProvider',
		);
	}
	return context;
}
